import { useCallback, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router";
import _ from "lodash";

import {
  CancelIcon,
  DescriptionIcon,
  DownloadOutlinedIcon,
  LaunchIcon,
} from "icons";
import theme from "theme";
import { colors } from "theme/palette";
import { openURLInNewTab } from "utils/common";
import { downloadFileWithFileName } from "utils/file";
import { handleInvalidRequest } from "utils/sdk";

import Button from "components/Button";
import { CircularProgress } from "components/Loading";
import Stack from "components/Stack";
import Text from "components/Text";
import toast from "components/Toast";
import Tooltip from "components/Tooltip";

import BouncingDotsLoader from "pages/Deal/BouncingDotsLoader";

import { useMemoPDFGenerationReadyEvent } from "entities/Deal/hooks";
import PDFEmbed from "entities/File/components/PDFEmbed";
import {
  memoPDFFileWithSignedUrlAndOriginalFileNameRetrieve,
  MemoPDFStatus,
  memoSignatureEmailNotificationProcessFinalize,
  useMemoPDFFileWithSignedUrlAndOriginalFileName,
  useMemoPDFSnapshotRetrieve,
} from "entities/Memo/sdk";

export interface IMemoESignatureContentAssignmentOverview {
  onClose: () => void;
}

const MemoESignaturePDFView = ({
  onClose,
}: IMemoESignatureContentAssignmentOverview) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const params = useParams<{
    dealId: string;
    memoId: string;
  }>();
  const dealId = Number(params.dealId);
  const memoId = Number(params.memoId);

  const [searchParams] = useSearchParams();

  const [isDownloading, setIsDownloading] = useState<boolean>(false);

  const memoPDFId = useMemo(() => {
    if (!_.isNil(searchParams) && searchParams.has("memoPDFId")) {
      return searchParams.get("memoPDFId");
    }
  }, [searchParams]);

  const formattedMemoPDFId = useMemo(
    () => (!_.isNil(memoPDFId) ? Number(memoPDFId) : undefined),
    [memoPDFId]
  );

  const { data: memoPDF, mutate: mutateMemoPDF } = useMemoPDFSnapshotRetrieve({
    dealId,
    memoId,
    memoPDFId: formattedMemoPDFId,
    enabled: !_.isNil(memoPDFId),
  });

  const { data: memoPDFFileSignedUrlAndOriginalFileName } =
    useMemoPDFFileWithSignedUrlAndOriginalFileName({
      dealId,
      memoId,
      memoPDFId: Number(formattedMemoPDFId),
      enabled: memoPDF?.status === MemoPDFStatus.SUCCEEDED,
    });

  const onMemoPDFGenerationUpdate = useCallback(
    () => mutateMemoPDF(),
    [mutateMemoPDF]
  );

  useMemoPDFGenerationReadyEvent({
    successEventHandler: onMemoPDFGenerationUpdate,
  });

  const onDownload = useCallback(
    ({ memoPDFId, fileName }: { memoPDFId: number; fileName: string }) => {
      setIsDownloading(true);
      memoPDFFileWithSignedUrlAndOriginalFileNameRetrieve({
        dealId,
        memoId,
        memoPDFId,
      }).then((data) => {
        const toastId = toast.loading("Retrieving file...");

        downloadFileWithFileName({
          fileUrl: data.url,
          fileName,
          toastId,
        })
          .catch(handleInvalidRequest)
          .finally(() => setIsDownloading(false));
      });
    },
    [dealId, memoId]
  );

  const handleMemoPDFFilePreview = useCallback(
    ({ memoPDFId }: { memoPDFId: number }) => {
      memoPDFFileWithSignedUrlAndOriginalFileNameRetrieve({
        dealId,
        memoId,
        memoPDFId,
      }).then(({ url }) => {
        openURLInNewTab({ url });
      });
    },
    [dealId, memoId]
  );

  const onSignatureProcessFinalize = useCallback(() => {
    if (_.isNil(formattedMemoPDFId) || isSubmitting) {
      return;
    }

    setIsSubmitting(true);

    memoSignatureEmailNotificationProcessFinalize({
      dealId,
      memoId,
      memoPDFId: formattedMemoPDFId,
    })
      .then(() => {
        onClose();
        toast.successMessage(
          "First order assignees have been successfully notified about the requested signature!"
        );
      })
      .catch(handleInvalidRequest)
      .finally(() => setIsSubmitting(false));
  }, [dealId, memoId, onClose, formattedMemoPDFId, isSubmitting]);

  return (
    <Stack
      sx={{
        flex: 1,
        padding: 3,
      }}
      gap={1.25}
    >
      <Stack
        alignItems="flex-start"
        sx={{
          padding: theme.spacing(1.5, 1.5),
          backgroundColor: colors.gray10,
          borderRadius: "8px",
        }}
      >
        <Text variant="text2" fontWeight={500}>
          This is the finalized PDF document that will get sent out to be
          signed.
        </Text>
        <Text variant="text4" color={colors.gray80}>
          Please do a quick review of the document and check if everything is as
          it should be. If anything needs to be modified, you can exit from this
          process, make edits and corrections to your document, then restart the
          signing process.
        </Text>
      </Stack>
      <Stack gap={2.5} flex={1}>
        <Stack direction="row" justifyContent="space-between">
          {memoPDF?.status === MemoPDFStatus.IN_PROGRESS && (
            <Stack
              direction="row"
              gap={1}
              padding={1.5}
              flex={1}
              borderRadius="8px"
              sx={{ backgroundColor: colors.blue20 }}
              alignItems="center"
            >
              <CircularProgress size={24} />
              <Text variant="text4" fontWeight={500} color={colors.blue100}>
                Document is being generated...
              </Text>
            </Stack>
          )}

          {memoPDF?.status === MemoPDFStatus.FAILED && (
            <Stack
              direction="row"
              gap={1}
              padding={1.5}
              flex={1}
              borderRadius="8px"
              sx={{ backgroundColor: colors.blue20 }}
              alignItems="center"
            >
              <CancelIcon
                sx={{
                  width: 16,
                  height: 16,
                  color: colors.red120,
                }}
              />
              <Text variant="text4" fontWeight={500} color={colors.red100}>
                Document generation has failed!
              </Text>
            </Stack>
          )}

          {memoPDF?.status === MemoPDFStatus.SUCCEEDED &&
            !_.isNil(memoPDF.file) && (
              <Stack
                direction="row"
                padding={1.5}
                justifyContent="space-between"
                flex={1}
                borderRadius="8px"
                sx={{ backgroundColor: colors.blue20 }}
                alignItems="center"
              >
                <Stack direction="row" alignItems="center" gap={1}>
                  <DescriptionIcon sx={{ width: "24px", heigth: "24px" }} />
                  <Stack direction="row" gap={1.5} maxWidth={700}>
                    <Tooltip title={memoPDF.file.original_file_name}>
                      <Text
                        variant="text4"
                        fontWeight={700}
                        noWrap
                        overflow="hidden"
                        textOverflow="ellipsis"
                      >
                        {memoPDF.file.original_file_name}
                      </Text>
                    </Tooltip>
                  </Stack>
                </Stack>
                <Stack direction="row" alignItems="center" gap={1}>
                  <Button
                    variant="outlined"
                    color="secondary"
                    sx={{
                      padding: theme.spacing(0.2, 1),
                      height: "fit-content",
                    }}
                    onClick={() =>
                      handleMemoPDFFilePreview({
                        memoPDFId: memoPDF.id,
                      })
                    }
                  >
                    <Stack direction="row" alignItems="center" gap={0.5}>
                      <LaunchIcon
                        sx={{
                          width: 16,
                          height: 16,
                        }}
                      />
                      <Text variant="text4" fontWeight={500}>
                        View
                      </Text>
                    </Stack>
                  </Button>
                  <Button
                    variant="outlined"
                    color="secondary"
                    sx={{
                      padding: theme.spacing(0.2, 1),
                      height: "fit-content",
                    }}
                    disabled={isDownloading}
                    onClick={() =>
                      !_.isNil(memoPDF.file) &&
                      onDownload({
                        memoPDFId: memoPDF.id,
                        fileName: memoPDF.file.original_file_name,
                      })
                    }
                  >
                    <Stack direction="row" alignItems="center" gap={0.5}>
                      <DownloadOutlinedIcon
                        sx={{
                          width: 16,
                          height: 16,
                        }}
                      />
                      <Text variant="text4" fontWeight={500}>
                        Download
                      </Text>
                    </Stack>
                  </Button>
                </Stack>
              </Stack>
            )}
        </Stack>
        {memoPDF?.status === MemoPDFStatus.SUCCEEDED &&
          !_.isNil(memoPDFFileSignedUrlAndOriginalFileName) && (
            <PDFEmbed
              pdfFileUrl={memoPDFFileSignedUrlAndOriginalFileName?.url}
              focusedQuoteClearanceCallback={() => {}}
              hideZoomControls
            />
          )}
        {memoPDF?.status === MemoPDFStatus.IN_PROGRESS && (
          <BouncingDotsLoader
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              position: "inherit",
            }}
          />
        )}
      </Stack>
      <Button
        loading={isSubmitting}
        disabled={memoPDF?.status !== MemoPDFStatus.SUCCEEDED}
        onClick={onSignatureProcessFinalize}
      >
        Send document & E-sign request to all
      </Button>
    </Stack>
  );
};

export default MemoESignaturePDFView;
